/**
 * Opaque chrome for {@link EditableField}, aligned with module structure editor
 * tile headers vs expanded accordion sections.
 *
 * @see apps/web/.../structure-editor/constants.ts
 *   ENTITY_STRUCTURE_HEADER_BG_CLASS / ENTITY_STRUCTURE_ACCORDION_BODY_BG_CLASS
 */

/** Rounded panel shell. */
export const EDITABLE_FIELD_PANEL_CLASS =
	"overflow-hidden rounded-xl border border-border";

/**
 * Multi-column shell — same chrome as {@link EDITABLE_FIELD_PANEL_CLASS} but
 * height must follow content (no height:% / overflow trap that clips the tallest column).
 */
export const EDITABLE_FIELD_COLUMNS_SHELL_CLASS =
	"min-h-min w-full overflow-hidden rounded-xl border border-border";

/**
 * Column root inside {@link EditableFieldColumns}.
 * Prefer content height + stretch — never `h-full` (that lets the shorter
 * column win the row size and clips the taller one).
 */
export const EDITABLE_FIELD_COLUMN_CLASS =
	"flex h-auto min-h-min min-w-0 flex-col self-stretch";

export type EditableFieldHeaderVariant = "plain" | "gradient";

/**
 * Flip this to `"plain"` to restore solid white / dark background headers.
 * Headers also accept a per-instance `variant` prop override.
 */
export const EDITABLE_FIELD_HEADER_VARIANT_DEFAULT: EditableFieldHeaderVariant =
	"gradient";

/**
 * Tile / field header — solid white (light) / page background (dark).
 * Always the base layer; gradient (when enabled) sits on top at 50% opacity.
 */
export const EDITABLE_FIELD_HEADER_BG_PLAIN_CLASS =
	"bg-white dark:bg-background";

/**
 * Soft brand wash overlay — top-left `#ffffff` → bottom-right `#DAD3DF` at 20%.
 * Dark: inverted charcoal → muted purple-grey (same diagonal), also 20%.
 * Applied as an absolutely positioned layer over {@link EDITABLE_FIELD_HEADER_BG_PLAIN_CLASS}.
 */
export const EDITABLE_FIELD_HEADER_GRADIENT_OVERLAY_CLASS =
	"pointer-events-none absolute inset-0 bg-gradient-to-br from-[#ffffff]/20 to-[#DAD3DF]/20 dark:from-[#313539]/20 dark:to-[#1A171F]/20";

/** @deprecated Prefer {@link EDITABLE_FIELD_HEADER_BG_PLAIN_CLASS}. */
export const EDITABLE_FIELD_HEADER_BG_CLASS =
	EDITABLE_FIELD_HEADER_BG_PLAIN_CLASS;

/** @deprecated Prefer {@link EDITABLE_FIELD_HEADER_GRADIENT_OVERLAY_CLASS}. */
export const EDITABLE_FIELD_HEADER_BG_GRADIENT_CLASS =
	EDITABLE_FIELD_HEADER_GRADIENT_OVERLAY_CLASS;

/**
 * Expanded accordion / section surface — solid page background.
 * Used for body and footers (body stays this colour while editing; only the
 * control itself uses {@link EDITABLE_FIELD_CONTROL_SURFACE_CLASS}).
 */
export const EDITABLE_FIELD_SECTION_BG_CLASS = "bg-background";

/**
 * Input / textarea fill — matches shadcn {@link Input} / {@link Textarea} (`bg-card`).
 */
export const EDITABLE_FIELD_INPUT_BG_CLASS = "bg-card";

/**
 * Bordered editable control surface (input, textarea, TipTap edit chrome).
 * Includes the same focus ring language as core shadcn form controls.
 */
export const EDITABLE_FIELD_CONTROL_SURFACE_CLASS =
	"rounded-md border border-input bg-card shadow-xs focus-within:border-ring focus-within:outline-hidden focus-within:ring-1 focus-within:ring-ring";

/** Compact header row (`h-11` = size-8 controls + `py-1.5`). */
export const EDITABLE_FIELD_HEADER_BAR_CLASS =
	"flex h-11 shrink-0 items-center gap-2 border-b border-border px-2";

export const EDITABLE_FIELD_HEADER_TITLE_CLASS =
	"shrink-0 px-1 text-xs font-black uppercase tracking-wide text-foreground";

export const EDITABLE_FIELD_HEADER_SEPARATOR_CLASS =
	"mx-0.5 h-5 w-px shrink-0 bg-border";

export const EDITABLE_FIELD_FOOTER_BAR_CLASS =
	"flex shrink-0 items-center border-t border-border px-2 py-1.5";

/** Vertical stack of editable field panels. */
export const EDITABLE_FIELD_STACK_CLASS = "flex flex-col gap-6";

export const EDITABLE_FIELD_SECTION_CLASS = "flex flex-col gap-0";

/**
 * Offset when scrolling an EditableField into view on pencil → edit
 * (`scrollIntoView` / scroll-parent math respects this as top padding).
 */
export const EDITABLE_FIELD_SCROLL_MARGIN_CLASS = "scroll-mt-4 sm:scroll-mt-6";
